import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { optionImageUrl, optionText } from '../../utils/authoredQuiz';

function tableCells(row) {
  if (Array.isArray(row)) return row;
  return Array.isArray(row?.cells) ? row.cells : [];
}

function Exhibit({ exhibit = [], passage = '' }) {
  const blocks = exhibit.length ? exhibit : (passage ? [{ type: 'text', text: passage }] : []);
  if (!blocks.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
      {blocks.map((block, index) => {
        if (block.type === 'image' && block.url) {
          return <img key={index} src={block.url} alt="" style={{ maxWidth: '100%', borderRadius: 12, border: '1px solid #e2e8f0' }} />;
        }
        if (block.type === 'table') {
          return (
            <div key={index} style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14 }}>
                <tbody>
                  {(block.rows || []).map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {tableCells(row).map((cell, cellIndex) => (
                        <td key={cellIndex} style={{ border: '1px solid #cbd5e1', padding: '8px 10px', background: rowIndex === 0 ? '#f8fafc' : '#fff' }}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (block.type === 'text' && block.text) {
          return <p key={index} style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: '#334155', whiteSpace: 'pre-wrap' }}>{block.text}</p>;
        }
        return null;
      })}
    </div>
  );
}

export default function ModuleExamPlayer({
  state,
  questions = [],
  answers = {},
  onAnswer,
  onSubmit,
  onProceed,
  onRetry,
  onContinueAnyway,
  onClose,
  color = '#6332ed',
  title = 'Module Exam',
  subtitle = '',
  passMark = 1,
  proceedLabel = 'Done',
  continueLabel = 'Move on',
  canSkip = false,
  canRetry = true,
  practiceOnly = false,
}) {
  const list = Array.isArray(questions) ? questions : [];
  const [index, setIndex] = useState(0);
  const [reviewing, setReviewing] = useState(false);
  const total = list.length;
  const score = list.filter((question, i) => answers[i] === question.correct).length;
  const passed = score >= passMark;
  const allAnswered = total > 0 && Object.keys(answers).length >= total;
  const question = list[Math.min(index, Math.max(total - 1, 0))];

  if (!state) return null;

  if (state === 'loading' || !question) {
    return createPortal(
      <div style={ui.page}>
        <h2 style={ui.title}>{state === 'loading' ? 'Preparing your Module Exam…' : 'Module exam unavailable'}</h2>
        <p style={ui.sub}>
          {state === 'loading'
            ? (subtitle || 'Loading the saved questions.')
            : 'This exam has no questions to show.'}
        </p>
        {onClose ? (
          <button type="button" onClick={onClose} style={{ ...ui.secondary, marginTop: 16 }}>Exit</button>
        ) : null}
      </div>,
      document.body,
    );
  }

  if (state === 'submitted' && !reviewing) {
    return createPortal(
      <div style={ui.page}>
        <div style={ui.card}>
          <h2 style={ui.title}>{passed ? 'Module exam passed' : 'Module exam result'}</h2>
          <p style={ui.score}>{score}/{total} · {total ? Math.round((score / total) * 100) : 0}%</p>
          <p style={ui.sub}>
            {passed
              ? `You reached the passing score of ${passMark}/${total}.`
              : practiceOnly
                ? `Score ${score}/${total}. You can retry this saved exam.`
                : `Need ${passMark}/${total} to pass.`}
          </p>
          <div style={ui.actions}>
            <button type="button" onClick={() => { setIndex(0); setReviewing(true); }} style={ui.secondary}>Review answers</button>
            {passed ? (
              <button type="button" onClick={onProceed} style={{ ...ui.primary, background: color }}>{practiceOnly ? 'Close' : proceedLabel}</button>
            ) : (
              <>
                {canRetry ? (
                  <button type="button" onClick={onRetry} style={{ ...ui.primary, background: color }}>Retry Module Exam</button>
                ) : null}
                {canSkip && !practiceOnly ? (
                  <button type="button" onClick={onContinueAnyway} style={ui.secondary}>{continueLabel}</button>
                ) : null}
                {practiceOnly ? (
                  <button type="button" onClick={onClose} style={ui.secondary}>Close</button>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  const submitted = state === 'submitted';
  const selected = answers[index];

  return createPortal(
    <div style={ui.page}>
      <div style={ui.top}>
        <button type="button" onClick={submitted ? () => setReviewing(false) : onClose} style={ui.secondary}>
          {submitted ? 'Back to report' : 'Exit'}
        </button>
        <div>
          <strong>{title}</strong>
          <div style={ui.sub}>{subtitle}</div>
        </div>
        <span style={ui.sub}>Question {index + 1} / {total}</span>
      </div>
      <div style={ui.body}>
        <div style={ui.nav}>
          {list.map((item, itemIndex) => {
            const answered = answers[itemIndex] !== undefined;
            const correct = submitted && answers[itemIndex] === item.correct;
            const wrong = submitted && answered && !correct;
            return (
              <button
                key={itemIndex}
                type="button"
                onClick={() => setIndex(itemIndex)}
                style={{
                  ...ui.dot,
                  background: correct ? '#16a34a' : wrong ? '#dc2626' : itemIndex === index ? color : answered ? '#e2e8f0' : '#fff',
                  color: correct || wrong || itemIndex === index ? '#fff' : '#334155',
                }}
              >
                {itemIndex + 1}
              </button>
            );
          })}
        </div>
        <div style={ui.questionCard}>
          <Exhibit exhibit={question.exhibit} passage={question.passage} />
          {question.questionImageUrl && !(question.exhibit || []).some((block) => block.type === 'image' && block.url === question.questionImageUrl) ? (
            <img src={question.questionImageUrl} alt={question.questionImageDescription || ''} style={{ maxWidth: '100%', borderRadius: 12, border: '1px solid #e2e8f0', marginBottom: 16 }} />
          ) : null}
          <h3 style={ui.stem}>{question.question}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(question.options || []).map((option, optionIndex) => {
              const text = optionText(option);
              const image = optionImageUrl(option);
              const isSelected = selected === optionIndex;
              const isCorrect = question.correct === optionIndex;
              let border = '#e2e8f0';
              let background = '#fff';
              if (submitted && isCorrect) {
                border = '#16a34a';
                background = '#f0fdf4';
              } else if (submitted && isSelected && !isCorrect) {
                border = '#dc2626';
                background = '#fef2f2';
              } else if (isSelected) {
                border = color;
                background = `${color}14`;
              }
              return (
                <button
                  key={optionIndex}
                  type="button"
                  disabled={submitted}
                  onClick={() => onAnswer(index, optionIndex)}
                  style={{ ...ui.option, borderColor: border, background }}
                >
                  <strong>{String.fromCharCode(65 + optionIndex)}</strong>
                  <span style={{ textAlign: 'left' }}>
                    {text ? <span style={{ display: 'block' }}>{text}</span> : null}
                    {image ? <img src={image} alt="" style={{ maxWidth: 220, marginTop: text ? 8 : 0, borderRadius: 8 }} /> : null}
                  </span>
                </button>
              );
            })}
          </div>
          {submitted && question.explanation ? (
            <p style={ui.explain}>{question.explanation}</p>
          ) : null}
          <div style={ui.actions}>
            <button type="button" onClick={() => setIndex((value) => Math.max(0, value - 1))} disabled={index === 0} style={ui.secondary}>Previous</button>
            {index < total - 1 ? (
              <button type="button" onClick={() => setIndex((value) => Math.min(total - 1, value + 1))} style={{ ...ui.primary, background: color }}>Next</button>
            ) : submitted ? null : (
              <button type="button" onClick={onSubmit} disabled={!allAnswered} style={{ ...ui.primary, background: color, opacity: allAnswered ? 1 : 0.45 }}>
                Submit exam
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const ui = {
  page: {
    position: 'fixed',
    inset: 0,
    zIndex: 4000,
    background: '#f8fafc',
    overflow: 'auto',
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    padding: 20,
  },
  top: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 16 },
  body: { display: 'grid', gridTemplateColumns: '180px 1fr', gap: 16, alignItems: 'start' },
  nav: { display: 'flex', flexWrap: 'wrap', gap: 6, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 12 },
  questionCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 22 },
  card: { maxWidth: 560, margin: '48px auto', background: '#fff', borderRadius: 20, padding: 32, border: '1px solid #e2e8f0', textAlign: 'center' },
  title: { margin: '0 0 8px', fontSize: 26, fontWeight: 800, color: '#1b1b1d' },
  score: { fontSize: 42, fontWeight: 900, margin: '8px 0' },
  stem: { margin: '0 0 16px', fontSize: 18, lineHeight: 1.5, color: '#1b1b1d' },
  sub: { color: '#64748b', fontSize: 13, lineHeight: 1.5 },
  explain: { marginTop: 16, background: '#f8fafc', borderRadius: 10, padding: 12, color: '#334155', fontSize: 14 },
  actions: { display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginTop: 18 },
  primary: { border: 'none', color: '#fff', borderRadius: 12, padding: '12px 16px', fontWeight: 700, cursor: 'pointer' },
  secondary: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 16px', fontWeight: 700, cursor: 'pointer', color: '#334155' },
  option: { display: 'flex', gap: 12, alignItems: 'flex-start', width: '100%', border: '1.5px solid', borderRadius: 12, padding: 12, cursor: 'pointer', font: 'inherit' },
  dot: { width: 36, height: 36, borderRadius: 8, border: '1px solid #e2e8f0', fontWeight: 700, cursor: 'pointer' },
};
